import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Col,
  Row,
  Button,
  Tour,
  Spin,
  message,
  Modal,
  Input,
  Form,
  Space,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import useCheckDesktop from 'hooks/useIsDesktop';
import dayjs from 'dayjs';
import api from 'constants/api';
import Api from 'util/Api';
import { downloadByRequest } from 'util/Tools';
import PackageInput from './components/PackageInput';
import AddCollectionModal from './components/AddCollectionModal';
import RouteStatistics from './components/Statistics';
import Map from './components/map';
import Result, { ResultType } from './components/Result';
import { useHistory, useParams } from 'react-router';
import {
  useCancelTransferByOperator,
  useCreateJourney,
  useDismantleJourney,
  useForceEndJourney,
  useOffer,
  useRedraft,
  OfferResponse,
  useUnassign,
} from 'services/journeys';
import { kindRules, JourneyState } from 'interfaces/journey';
import SaveTemplateButton from './components/SaveTemplateButton';
import JourneyName from './components/JourneyName';
import SaveDraftModal from './components/SaveDraftModal';
import useCollectionsEnabled from 'hooks/useCollectionsEnabled';
import WaypointsList from './components/WaypointsList';
import { JourneyProvider, useJourneyDashboardContext } from './context';
import Driver from './components/Driver';
import AuditLog from './components/AuditLog';
import RouteProgress from './components/RouteProgress';
import Title from 'antd/es/typography/Title';
import EndpointsSelector from './components/EndpointsSelector';
import ScheduleSelector from './components/ScheduleSelector';
import CollectionsCard from './components/CollectionsCard';
import OfferAtRiskModal from './components/OfferAtRiskModal';
import { forgetLastDraft, lastDraft, rememberLastDraft } from '../lastDraft';
import FuseRoutesButton from './components/FuseRoutesButton';
import TransferRouteButton from './components/TransferRouteButton';

const { confirm } = Modal;

const JourneyWrapper = () => {
  const { id } = useParams<{ id: string }>();
  return (
    <JourneyProvider id={id}>
      <JourneyDashboard />
    </JourneyProvider>
  );
};

const JourneyDashboard = () => {
  const history = useHistory();
  const isDesktop = useCheckDesktop();
  const {
    hasError,
    id,
    state,
    loading,
    driver,
    creationDate,
    shipmentCount,
    flaggedShipments,
    schedule,
    totalCollections,
    kind,
  } = useJourneyDashboardContext();
  const { mutateAsync: createJourney } = useCreateJourney();
  const { mutateAsync: dismantleJourney } = useDismantleJourney();
  const { mutate: forceEndRouteMutate, isPending: isLoadingForceEnd } =
    useForceEndJourney();
  const { mutate: cancelTransfer, isPending: isLoadingCancelTransfer } =
    useCancelTransferByOperator();
  const { mutateAsync: offerJourney } = useOffer();
  const { mutateAsync: redraftJourney } = useRedraft();
  const { mutateAsync: unassignJourney } = useUnassign();

  const [result, setResult] = useState<ResultType | undefined>(undefined);
  const [addCollectionOpen, setAddCollectionOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const collectionsEnabled = useCollectionsEnabled();
  const rules = kindRules(kind);
  const hasCollections = collectionsEnabled && rules.collections;
  const hasShipments = rules.shipments;
  const actionCount = (shipmentCount ?? 0) + (totalCollections ?? 0);
  const [tourOpen, setTourOpen] = useState(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [forceEndModalOpen, setForceEndModalOpen] = useState(false);
  const [downloadingRoadsheet, setDownloadingRoadsheet] = useState(false);
  const [offerAtRiskModalOpen, setOfferAtRiskModalOpen] = useState(false);
  const [isOfferingLoading, setIsOfferingLoading] = useState(false);
  const [forceEndForm] = Form.useForm();

  useEffect(() => {
    if (!id || !state) return;
    const routeKind = kind ?? 'shipments';
    // Solo se recuerda la ruta creada; se olvida si deja de ser borrador.
    if (state !== JourneyState.Draft && lastDraft(routeKind) === id)
      forgetLastDraft(routeKind);
  }, [id, state, kind]);

  useEffect(() => {
    if (hasError)
      setResult({
        status: 'error',
        title: 'Ocurrió un error',
        subTitle: 'No se pudo cargar la ruta',
      } as ResultType);
  }, [hasError]);

  const handleNewRoute = async () => {
    try {
      const response = await createJourney(kind ?? 'shipments');
      if (response?.journeyId) {
        rememberLastDraft(kind ?? 'shipments', response.journeyId);
        history.push('/journey/' + response.journeyId);
      }
    } catch (error) {
      console.error(error);
      message.error('Ocurrió un error al crear la ruta');
    } finally {
      setResult(undefined);
    }
  };

  const handleForceEnd = () => {
    setForceEndModalOpen(true);
  };

  const handleDownloadRoadsheet = async () => {
    if (!id) return;
    setDownloadingRoadsheet(true);
    try {
      const response = await Api.get(api.journeys.roadsheet(id), {
        responseType: 'blob',
      });
      if (!Api.isSuccessResponse(response))
        throw new Error(`Status: ${response.status}`);
      const url = URL.createObjectURL(response.data);
      downloadByRequest(url, `ruta-${id}.csv`);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      message.error('No se pudo descargar la hoja de ruta');
    } finally {
      setDownloadingRoadsheet(false);
    }
  };

  const handleCancelTransfer = () => {
    if (id) cancelTransfer(id);
  };

  const handleForceEndConfirm = async () => {
    try {
      const values = await forceEndForm.validateFields();
      if (id) {
        forceEndRouteMutate(
          { id, endReasonComment: values.endReasonComment },
          {
            onSuccess() {
              history.push('/journeys');
              setForceEndModalOpen(false);
              forceEndForm.resetFields();
            },
          },
        );
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleForceEndCancel = () => {
    setForceEndModalOpen(false);
    forceEndForm.resetFields();
  };

  const handleDismantle = () => {
    confirm({
      title: '¿Está seguro/a de que desea desarmar la ruta?',
      content: 'Esta acción no se puede deshacer.',
      okText: 'Sí',
      cancelText: 'No',
      onOk: async () => {
        if (id) {
          setDisabled(true);
          dismantleJourney(id)
            .then(() => {
              history.push('/journeys');
            })
            .finally(() => setDisabled(false));
        }
      },
    });
  };

  const performOffer = async () => {
    setIsOfferingLoading(true);
    try {
      const response = (await offerJourney(id!)) as OfferResponse;
      const extraButtons = [
        <Button type="text" onClick={() => history.push('/journeys')}>
          Volver al listado
        </Button>,
        <Button
          onClick={() => {
            history.push(`/journey/${id}`);
            setResult(undefined);
          }}
        >
          Ver ruta
        </Button>,
        <Button onClick={handleNewRoute} type="primary">
          Nueva ruta
        </Button>,
      ];

      const subTitleContent = (
        <>
          <p>Fecha y hora: {dayjs().format('DD/MM/YY HH:mm:ss')}</p>
          {driver && <p>Driver: {driver.email}</p>}
        </>
      );

      setResult({
        status: 'success',
        title: driver
          ? `Ruta ${id} ofrecida a ${driver.fullname}`
          : `Ruta ${id} ofertada`,
        subTitle: subTitleContent,
        extraButtons: extraButtons,
      });
      setOfferAtRiskModalOpen(false);
    } catch (e: any) {
      setResult({
        status: 'error',
        title: `Ocurrió un error al ${driver?.id ? 'ofrecer' : 'ofertar'} la ruta ${id}`,
        subTitle: e.message,
        extraButtons: [
          <Button type="text" onClick={() => history.push('/journeys')}>
            Volver al listado
          </Button>,
          <Button onClick={() => setResult(undefined)}>Reintentar</Button>,
        ],
      });
    } finally {
      setIsOfferingLoading(false);
    }
  };

  const handleOffer = () => {
    if (flaggedShipments && flaggedShipments.length > 0) {
      setOfferAtRiskModalOpen(true);
    } else {
      confirm({
        title: `¿Está seguro/a de que desea ${driver?.id ? 'ofrecer' : 'ofertar'} la ruta?`,
        content: `Podrá volver la ruta a preparación al inspeccionar la ruta en el listado de seguimiento.${driver?.id ? `\n Driver: ${driver?.name}` : ''}`,
        okText: 'Sí',
        cancelText: 'No',
        onOk: performOffer,
      });
    }
  };

  const handleRedraft = () => {
    confirm({
      title: `¿Está seguro/a de que desea ${driver?.id ? 'desofrecer' : 'desofertar'} la ruta?`,
      content: `Podrá volver a ${driver?.id ? 'ofrecer' : 'ofertar'} la ruta al inspeccionarla en el listado de preparación.`,
      okText: 'Sí',
      cancelText: 'No',
      onOk() {
        return redraftJourney(id!)
          .then(() => {
            const extraButtons = [
              <Button type="text" onClick={() => history.push('/journeys')}>
                Volver al listado
              </Button>,
              <Button
                onClick={() => {
                  history.push(`/journey/${id}`);
                  setResult(undefined);
                }}
              >
                Ver ruta
              </Button>,
              <Button onClick={handleNewRoute} type="primary">
                Nueva ruta
              </Button>,
            ];
            setResult({
              status: 'success',
              title: `Ruta ${id} ${driver?.id ? 'desofrecida' : 'desofertada'} correctamente`,
              extraButtons: extraButtons,
            });
          })
          .catch((e) =>
            // Error result
            setResult({
              status: 'error',
              title: `Ocurrió un error al ${driver?.id ? 'desofrecer' : 'desofertar'} la ruta ${id}`,
              subTitle: e.message,
              extraButtons: [
                <Button
                  type="text"
                  onClick={() => history.push('/journeys/active')}
                >
                  Volver al listado
                </Button>,
                <Button onClick={() => setResult(undefined)}>
                  Reintentar
                </Button>,
              ],
            }),
          );
      },
    });
  };

  const handleRelease = () => {
    confirm({
      title: '¿Está seguro/a de que desea liberar la ruta?',
      content:
        'El driver la pierde; según de dónde vino, vuelve a ofertada o a preparación.',
      okText: 'Sí',
      cancelText: 'No',
      onOk() {
        return unassignJourney({ journeyId: id!, driverId: driver?.id! })
          .then(() => {
            const extraButtons = [
              <Button type="text" onClick={() => history.push('/journeys')}>
                Volver al listado
              </Button>,
              <Button
                onClick={() => {
                  history.push(`/journey/${id}`);
                  setResult(undefined);
                }}
              >
                Ver ruta
              </Button>,
              <Button onClick={handleNewRoute} type="primary">
                Nueva ruta
              </Button>,
            ];
            setResult({
              status: 'success',
              title: `Ruta ${id} liberada correctamente`,
              extraButtons: extraButtons,
            });
          })
          .catch((e) =>
            setResult({
              status: 'error',
              title: `Ocurrió un error al liberar la ruta ${id}`,
              subTitle: e.message,
              extraButtons: [
                <Button
                  type="text"
                  onClick={() => history.push('/journeys/active')}
                >
                  Volver al listado
                </Button>,
                <Button onClick={() => setResult(undefined)}>
                  Reintentar
                </Button>,
              ],
            }),
          );
      },
    });
  };

  const refInput = useRef(null);
  const refCollections = useRef(null);
  const refProgress = useRef(null);
  const refTable = useRef(null);
  const refMap = useRef(null);
  const refStats = useRef(null);
  const refDriver = useRef(null);
  const refAudit = useRef(null);
  const refButton = useRef(null);
  const refEndpoints = useRef(null);
  const refSchedule = useRef(null);
  const refName = useRef(null);
  const refActions = useRef(null);
  const [statsHeight, setStatsHeight] = useState<number>();

  useEffect(() => {
    const handleResize = () => {
      if (refStats.current) {
        const height = (refStats.current as HTMLDivElement).offsetHeight;
        setStatsHeight(height);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isActiveOrTransfering =
    state === JourneyState.Started || state === JourneyState.Transfering;

  const offerLabel =
    state === JourneyState.Draft
      ? driver?.id
        ? 'Ofrecer'
        : 'Ofertar'
      : driver?.id
        ? 'Desofrecer'
        : 'Desofertar';

  const showOfferButton =
    state !== JourneyState.Accepted &&
    state !== JourneyState.Started &&
    state !== JourneyState.Transfering;

  const nav = {
    nextButtonProps: { children: <RightOutlined /> },
    prevButtonProps: { children: <LeftOutlined /> },
  };
  const steps = [
    {
      ...nav,
      title: 'Las paradas de la ruta',
      description:
        'Acá se listan las paradas en el orden en que se van a recorrer, con el estado de cada una. Arriba y abajo, de dónde sale y adónde vuelve la ruta.',
      target: () => refTable.current,
    },
    {
      ...nav,
      title: 'El recorrido en el mapa',
      description:
        'Cada parada es un pin. Tocá la brújula de una parada para centrar el mapa en ella.',
      target: () => refMap.current,
    },
    {
      ...nav,
      title: 'Estadísticas en tiempo real',
      description:
        'Estado, cantidad de paradas, tiempo estimado y distancia se recalculan con cada cambio.',
      target: () => refStats.current,
    },
    {
      ...nav,
      title: 'El driver',
      description:
        'Asignale un driver, o dejá la ruta ofertada para que la tome uno desde la app. En una ruta de colectas la patente se confirma al asignar.',
      target: () => refDriver.current,
    },
    {
      ...nav,
      title: 'Inicio y final de la ruta',
      description:
        'De dónde sale y adónde vuelve. Por defecto, el centro de distribución.',
      target: () => refEndpoints.current,
    },
    {
      ...nav,
      title: 'Horario',
      description:
        'Desde qué hora y hasta qué hora se recorre. Si con las paradas no llega, el ruteo sugiere una hora de fin, para aceptar o descartar.',
      target: () => refSchedule.current,
    },
    {
      ...nav,
      title: 'Los botones de la ruta',
      description:
        state === JourneyState.Draft
          ? `Descargar hoja de ruta baja las paradas en un archivo. ${
              hasCollections
                ? 'Guardar como plantilla guarda los puntos con sus horarios y el driver para volver a armarla.'
                : 'Unir rutas trae a esta las paradas de otro borrador.'
            } Desarmar borra el borrador. Ofertar la publica en la app; con driver asignado, Ofrecer se la ofrece a él.`
          : 'Según el estado: Desofertar la vuelve a borrador, Liberar ruta le saca el driver, Transferir se la pasa a otro driver en camino y Terminar ruta la cierra a la fuerza.',
      target: () => refActions.current,
    },
    {
      ...nav,
      title: 'Historial de auditoría',
      description: 'Todo lo que pasó con la ruta, cuándo y quién lo hizo.',
      target: () => refAudit.current,
    },
    {
      ...nav,
      nextButtonProps: { children: <CheckOutlined /> },
      title: 'Para volver a ver este tutorial',
      description: 'Tocá el botón de información cuando quieras repetirlo.',
      target: () => refButton.current,
    },
  ];

  const draftSteps = [
    hasCollections
      ? {
          ...nav,
          title: 'Agregá puntos de colecta',
          description:
            'Elegís un punto de colecta y uno de sus turnos. La colecta nace en esta ruta con esa hora y esa duración.',
          target: () => refCollections.current,
        }
      : {
          ...nav,
          title: 'Ingresá el código externo del paquete',
          description:
            'Escaneá o escribí el código y el paquete se suma a la ruta como una parada.',
          target: () => refInput.current,
        },
    {
      ...nav,
      title: 'Ponele nombre a la ruta',
      description:
        'Tocá el lápiz al lado del id. Sirve para encontrarla en los listados; solo se cambia en borrador.',
      target: () => refName.current,
    },
    ...steps,
  ];

  const startedSteps = [
    {
      ...nav,
      title: 'El progreso de la ruta',
      description:
        'La barra avanza a medida que el driver resuelve las paradas.',
      target: () => refProgress.current,
    },
    ...steps,
  ];

  const actionButtons = (
    <Row gutter={8} justify={'end'}>
      <Col>
        <Button
          size="small"
          icon={<DownloadOutlined />}
          onClick={handleDownloadRoadsheet}
          disabled={!id || actionCount === 0 || downloadingRoadsheet}
          loading={downloadingRoadsheet}
        >
          Descargar hoja de ruta
        </Button>
      </Col>
      {hasCollections && state === JourneyState.Draft && (
        <Col>
          <SaveTemplateButton
            disabled={disabled}
            empty={(totalCollections ?? 0) === 0}
          />
        </Col>
      )}
      {state === JourneyState.Draft && hasShipments && (
        <Col>
          <FuseRoutesButton
            journeyId={id}
            mode="dashboard"
            disabled={disabled}
          />
        </Col>
      )}
      {state === JourneyState.Started && (
        <Col>
          <TransferRouteButton journeyId={id} disabled={disabled} />
        </Col>
      )}
      {state === JourneyState.Transfering && (
        <Col>
          <Button
            size="small"
            icon={<CloseOutlined />}
            onClick={handleCancelTransfer}
            disabled={isLoadingCancelTransfer || !id}
            loading={isLoadingCancelTransfer}
          >
            Cancelar transferencia
          </Button>
        </Col>
      )}
      <Col>
        {isActiveOrTransfering ? (
          <Button
            danger
            size="small"
            onClick={handleForceEnd}
            disabled={isLoadingForceEnd || !id}
            loading={isLoadingForceEnd}
            icon={<DeleteOutlined />}
          >
            Terminar ruta
          </Button>
        ) : (
          <Button
            danger
            size="small"
            onClick={handleDismantle}
            disabled={disabled || !id}
            loading={disabled}
            icon={<DeleteOutlined />}
          >
            Desarmar
          </Button>
        )}
      </Col>
      {state === JourneyState.Draft && (
        <Col>
          <Button
            size="small"
            icon={<SaveOutlined />}
            onClick={() => setSaveOpen(true)}
            disabled={disabled || !id}
          >
            Guardar
          </Button>
        </Col>
      )}
      {showOfferButton && (
        <Col>
          <Button
            htmlType="submit"
            size="small"
            type="primary"
            danger={state !== JourneyState.Draft}
            onClick={() => {
              if (id)
                state === JourneyState.Draft ? handleOffer() : handleRedraft();
            }}
            disabled={disabled || actionCount === 0 || !id}
            loading={disabled}
          >
            {offerLabel}
          </Button>
        </Col>
      )}
      {state === JourneyState.Accepted && (
        <Col>
          <Button
            size="small"
            type="primary"
            danger
            onClick={handleRelease}
            disabled={disabled || !id || !driver?.id}
            loading={disabled}
          >
            Liberar ruta
          </Button>
        </Col>
      )}
    </Row>
  );

  const topBar = (
    <Row align="middle">
      <Col span={4}>
        <Row gutter={8}>
          <Col>
            <Button
              icon={<ArrowLeftOutlined />}
              size="small"
              onClick={() => {
                switch (state) {
                  case JourneyState.Draft:
                    history.push('/journeys/draft');
                    break;
                  case JourneyState.Finished:
                    history.push('/journeys/history');
                    break;
                  default:
                    history.push('/journeys/active');
                    break;
                }
              }}
            >
              Volver al listado
            </Button>
          </Col>
          <Col>
            <Button
              icon={<InfoCircleOutlined />}
              size="small"
              disabled={disabled}
              ref={refButton}
              onClick={() => setTourOpen(true)}
            />
          </Col>
        </Row>
      </Col>
      <Col span={6}>
        <Row justify={'center'} align={'middle'}>
          <div ref={refName}>
            <JourneyName />
          </div>
        </Row>
      </Col>
      <Col span={14}>
        <div ref={refActions}>
          {state !== JourneyState.Finished && actionButtons}
        </div>
      </Col>
    </Row>
  );

  return (
    <Spin spinning={loading}>
      <Result result={result} onRetry={() => history.push('/journeys')}>
        <Row gutter={[0, 8]} justify="end">
          <Col span={24}>{topBar}</Col>
          <Col span={24}>
            <Row gutter={8}>
              <Col xs={24} md={10}>
                <Row gutter={[8, 8]}>
                  <Col span={3}>
                    <AuditLog mode="dashboard" journeyId={id} ref={refAudit} />
                  </Col>
                  <Col span={21}>
                    {state === JourneyState.Draft ? (
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {hasShipments && (
                          <PackageInput ref={refInput} disabled={disabled} />
                        )}
                        {hasCollections && (
                          <div ref={refCollections}>
                            <CollectionsCard
                              disabled={false}
                              onAdd={() => setAddCollectionOpen(true)}
                            />
                          </div>
                        )}
                      </Space>
                    ) : (
                      <RouteProgress ref={refProgress} />
                    )}
                  </Col>
                  <Col span={24}>
                    <WaypointsList ref={refTable} />
                  </Col>
                </Row>
              </Col>
              {isDesktop && (
                <Col xs={24} md={14}>
                  <Row gutter={[0, 8]}>
                    <Col span={24}>
                      <Row gutter={8}>
                        <Col span={16}>
                          <RouteStatistics ref={refStats} />
                        </Col>
                        <Col span={8}>
                          <Driver
                            ref={refDriver}
                            disabled={disabled}
                            height={statsHeight}
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col span={24}>
                      <Row gutter={[8, 8]}>
                        <Col span={12}>
                          <EndpointsSelector
                            disabled={state !== JourneyState.Draft}
                            ref={refEndpoints}
                          />
                        </Col>
                        <Col span={12}>
                          <ScheduleSelector
                            disabled={state !== JourneyState.Draft}
                            ref={refSchedule}
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col span={24}>
                      <Map ref={refMap} />
                    </Col>
                  </Row>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
        <Tour
          disabledInteraction
          open={tourOpen}
          onClose={() => setTourOpen(false)}
          steps={state === JourneyState.Draft ? draftSteps : startedSteps}
        />
        <Modal
          title="Terminar ruta"
          open={forceEndModalOpen}
          onOk={handleForceEndConfirm}
          onCancel={handleForceEndCancel}
          okText="Terminar"
          cancelText="Cancelar"
          confirmLoading={isLoadingForceEnd}
        >
          <p>
            ¿Está seguro/a de que desea terminar la ruta? Esta acción no se
            puede deshacer.
          </p>
          <Form form={forceEndForm} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item
              name="endReasonComment"
              label="Motivo de terminación"
              rules={[
                {
                  required: true,
                  message: 'Por favor ingrese el motivo de terminación',
                },
              ]}
            >
              <Input.TextArea
                placeholder="Ingrese el motivo por el cual está terminando la ruta"
                rows={3}
              />
            </Form.Item>
          </Form>
        </Modal>
        <OfferAtRiskModal
          open={offerAtRiskModalOpen}
          items={flaggedShipments || []}
          routeSchedule={schedule}
          confirmLoading={isOfferingLoading}
          hasDriver={!!driver?.id}
          onCancel={() => setOfferAtRiskModalOpen(false)}
          onConfirm={performOffer}
        />
        {state === JourneyState.Draft && id && (
          <SaveDraftModal
            open={saveOpen}
            journeyId={id}
            kind={kind}
            shipments={shipmentCount ?? 0}
            collections={totalCollections ?? 0}
            onClose={() => setSaveOpen(false)}
          />
        )}
        {hasCollections && state === JourneyState.Draft && id && (
          <AddCollectionModal
            open={addCollectionOpen}
            journeyId={id}
            onClose={() => setAddCollectionOpen(false)}
          />
        )}
      </Result>
    </Spin>
  );
};

export default JourneyWrapper;
